import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Form,
  Input,
  Upload,
  message,
  Row,
  Col,
  Divider,
  Typography,
  Popconfirm,
  Modal,
  Alert,
  Tag,
  DatePicker,
  TimePicker,
  Select,
} from 'antd';
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  SettingOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import authService from '../utils/auth';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const API = `${API_BASE}/group-tickets`;

// 日期/时间一律用组件选，落到 PDF 时转成行程单惯用的写法。
// 月份不走 dayjs 的 MMM —— 全局 locale 是 zh-cn 时它会输出「7月」，这里必须是 JUL。
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const pad2 = (n) => String(n).padStart(2, '0');
// 16 JUL 2026
const fmtIssueDate = (d) => (d ? `${pad2(d.date())} ${MONTHS[d.month()]} ${d.year()}` : '');
// 19JUL26
const fmtSegDate = (d) => (d ? `${pad2(d.date())}${MONTHS[d.month()]}${String(d.year()).slice(-2)}` : '');
// 2045
const fmtTime = (t) => (t ? `${pad2(t.hour())}${pad2(t.minute())}` : '');
// 0950+1
const fmtArrive = (t, dayOffset) => (t ? `${fmtTime(t)}${dayOffset ? `+${dayOffset}` : ''}` : '');

// 纯文本列（日期/时间列单独渲染成组件）
const SEGMENT_TEXT_FIELDS = [
  { key: 'route', title: 'Origin/Dest', width: 120, placeholder: 'AKL/CKG' },
  { key: 'flight', title: 'Flight', width: 100, placeholder: 'HU786' },
  { key: 'classCode', title: 'Class', width: 80, placeholder: 'G' },
];
const SEGMENT_TAIL_FIELDS = [
  { key: 'status', title: 'Status', width: 90, placeholder: 'OK' },
  { key: 'baggage', title: 'Baggage Allow', width: 110, placeholder: '2PC' },
  { key: 'terminalFrom', title: 'Terminal Takeoff', width: 120, placeholder: 'T3' },
  { key: 'terminalTo', title: 'Arrival', width: 100, placeholder: 'T3' },
];
// 判空用：文本字段 + 组件字段
const SEGMENT_ALL_KEYS = [
  ...SEGMENT_TEXT_FIELDS.map((f) => f.key),
  'date', 'departTime', 'arriveTime',
  ...SEGMENT_TAIL_FIELDS.map((f) => f.key),
];

const emptySegment = () => ({
  _key: `seg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  route: '',
  flight: '',
  classCode: '',
  date: null,          // dayjs
  departTime: null,    // dayjs
  arriveTime: null,    // dayjs
  arriveDayOffset: 0,  // 到达跨日：0 / 1 / 2
  status: 'OK',
  baggage: '2PC',
  terminalFrom: '',
  terminalTo: '',
});

const emptyPassenger = () => ({
  _key: `pax-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: '',
  passportNumber: '',
  ticketNumber: '',
});

function GroupTicketItinerary() {
  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();

  const [passengers, setPassengers] = useState([emptyPassenger()]);
  const [segments, setSegments] = useState([emptySegment()]);
  const [parsing, setParsing] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  const headers = useCallback(() => authService.getAuthHeaders(), []);

  const loadLogo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/logo`, {
        headers: authService.getAuthHeaders(),
        responseType: 'blob',
      });
      setLogoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch (e) {
      setLogoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`, { headers: authService.getAuthHeaders() });
      if (res.data.success) {
        const s = res.data.data;
        setSettings(s);
        // 默认值只在表单还没被填过时带入，避免覆盖正在编辑的内容
        form.setFieldsValue({
          issuingAirline: form.getFieldValue('issuingAirline') || s.issuingAirline,
          issuingAgent: form.getFieldValue('issuingAgent') || s.issuingAgent,
          tel: form.getFieldValue('tel') || s.tel,
          fax: form.getFieldValue('fax') || s.fax,
          iataCode: form.getFieldValue('iataCode') || s.iataCode,
          formOfPayment: form.getFieldValue('formOfPayment') || s.formOfPayment,
          restrictions: form.getFieldValue('restrictions') || s.restrictions,
          idPrefix: form.getFieldValue('idPrefix') !== undefined ? form.getFieldValue('idPrefix') : s.idPrefix,
        });
        loadLogo();
      }
    } catch (e) {
      message.error(e.response?.data?.error || '读取默认设置失败');
    }
  }, [form, loadLogo]);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 卸载时释放 blob，别让预览图一直占着内存
  useEffect(() => () => { if (logoUrl) URL.revokeObjectURL(logoUrl); }, [logoUrl]);

  // -------------------------------------------------------------- Excel

  const handleExcel = async (file) => {
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/parse-excel`, fd, { headers: headers() });
      if (res.data.success) {
        const list = res.data.data.passengers.map((p) => ({ ...emptyPassenger(), ...p }));
        setPassengers(list);
        const cols = res.data.data.columns;
        const missing = [];
        if (!cols.passportNumber) missing.push('护照号');
        if (!cols.ticketNumber) missing.push('票号');
        if (missing.length) {
          message.warning(`已读取 ${list.length} 位乘客，但表格里没有${missing.join('、')}列，请在下方手动补齐`);
        } else {
          message.success(`已读取 ${list.length} 位乘客`);
        }
      }
    } catch (e) {
      message.error(e.response?.data?.error || '解析 Excel 失败');
    } finally {
      setParsing(false);
    }
    return false; // 阻止 antd 自行上传
  };

  // -------------------------------------------------------------- 行编辑

  const updatePassenger = (key, field, value) => {
    setPassengers((list) => list.map((p) => (p._key === key ? { ...p, [field]: value } : p)));
  };

  const updateSegment = (key, field, value) => {
    setSegments((list) => list.map((s) => (s._key === key ? { ...s, [field]: value } : s)));
  };

  const idPrefix = Form.useWatch('idPrefix', form) ?? '';

  const passengerColumns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    {
      title: 'NAME（姓名）',
      dataIndex: 'name',
      render: (v, r) => (
        <Input
          value={v}
          placeholder="ZHAO/XIU YUN"
          onChange={(e) => updatePassenger(r._key, 'name', e.target.value)}
        />
      ),
    },
    {
      title: '护照号',
      dataIndex: 'passportNumber',
      width: 180,
      render: (v, r) => (
        <Input
          value={v}
          placeholder="LT752016"
          onChange={(e) => updatePassenger(r._key, 'passportNumber', e.target.value)}
        />
      ),
    },
    {
      title: 'ID NUMBER（生成值）',
      width: 160,
      render: (_, r) => (
        <Text code style={{ fontSize: 12 }}>
          {r.passportNumber ? `${idPrefix}${r.passportNumber}` : '-'}
        </Text>
      ),
    },
    {
      title: 'ETKT NBR（票号）',
      dataIndex: 'ticketNumber',
      width: 200,
      render: (v, r) => (
        <Input
          value={v}
          placeholder="880-2425834456"
          onChange={(e) => updatePassenger(r._key, 'ticketNumber', e.target.value)}
        />
      ),
    },
    {
      title: '操作',
      width: 70,
      render: (_, r) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => setPassengers((list) => list.filter((p) => p._key !== r._key))}
        />
      ),
    },
  ];

  const textCol = (f) => ({
    title: f.title,
    dataIndex: f.key,
    width: f.width,
    render: (v, r) => (
      <Input
        value={v}
        placeholder={f.placeholder}
        onChange={(e) => updateSegment(r._key, f.key, e.target.value)}
      />
    ),
  });

  const segmentColumns = [
    { title: '#', width: 44, render: (_, __, i) => i + 1 },
    ...SEGMENT_TEXT_FIELDS.map(textCol),
    {
      // 显示即最终写法（19JUL26），所见即所得
      title: 'Date',
      dataIndex: 'date',
      width: 150,
      render: (v, r) => (
        <DatePicker
          value={v}
          format={fmtSegDate}
          allowClear
          style={{ width: '100%' }}
          placeholder="19JUL26"
          onChange={(d) => updateSegment(r._key, 'date', d)}
        />
      ),
    },
    {
      title: 'DEPT Time',
      dataIndex: 'departTime',
      width: 110,
      render: (v, r) => (
        <TimePicker
          value={v}
          format="HHmm"
          needConfirm={false}
          allowClear
          style={{ width: '100%' }}
          placeholder="2045"
          onChange={(t) => updateSegment(r._key, 'departTime', t)}
        />
      ),
    },
    {
      title: 'Arrival Time',
      dataIndex: 'arriveTime',
      width: 190,
      render: (v, r) => (
        <Space.Compact style={{ width: '100%' }}>
          <TimePicker
            value={v}
            format="HHmm"
            needConfirm={false}
            allowClear
            style={{ width: 100 }}
            placeholder="0950"
            onChange={(t) => updateSegment(r._key, 'arriveTime', t)}
          />
          <Select
            value={r.arriveDayOffset || 0}
            style={{ width: 84 }}
            onChange={(n) => updateSegment(r._key, 'arriveDayOffset', n)}
            options={[
              { value: 0, label: '当日' },
              { value: 1, label: '+1 天' },
              { value: 2, label: '+2 天' },
            ]}
          />
        </Space.Compact>
      ),
    },
    ...SEGMENT_TAIL_FIELDS.map(textCol),
    {
      title: '操作',
      width: 70,
      fixed: 'right',
      render: (_, r) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => setSegments((list) => list.filter((s) => s._key !== r._key))}
        />
      ),
    },
  ];

  // -------------------------------------------------------------- 生成

  const validPassengers = useMemo(
    () => passengers.filter((p) => p.name || p.passportNumber || p.ticketNumber),
    [passengers],
  );

  const buildPayload = async () => {
    const values = await form.validateFields();
    const prefix = values.idPrefix || '';
    return {
      common: {
        airlinePnr: values.airlinePnr || '',
        systemPnr: values.systemPnr || '',
        issuingAirline: values.issuingAirline || '',
        issuingAgent: values.issuingAgent || '',
        tel: values.tel || '',
        fax: values.fax || '',
        iataCode: values.iataCode || '',
        dateOfIssue: fmtIssueDate(values.dateOfIssue),
        formOfPayment: values.formOfPayment || '',
        restrictions: values.restrictions || '',
      },
      segments: segments
        .filter((s) => SEGMENT_ALL_KEYS.some((k) => (s[k] && String(s[k]).trim())))
        .map((s) => ({
          route: s.route,
          flight: s.flight,
          classCode: s.classCode,
          date: fmtSegDate(s.date),
          departTime: fmtTime(s.departTime),
          arriveTime: fmtArrive(s.arriveTime, s.arriveDayOffset),
          status: s.status,
          baggage: s.baggage,
          terminalFrom: s.terminalFrom,
          terminalTo: s.terminalTo,
        })),
      passengers: validPassengers.map((p) => ({
        name: p.name,
        idNumber: p.passportNumber ? `${prefix}${p.passportNumber}` : '',
        ticketNumber: p.ticketNumber,
      })),
      fileName: `ITINERARY_${values.airlinePnr || ''}`,
    };
  };

  const handleGenerate = async () => {
    if (!validPassengers.length) {
      message.warning('请先添加乘客');
      return;
    }
    let payload;
    try {
      payload = await buildPayload();
    } catch (e) {
      message.warning('请先补齐必填项');
      return;
    }
    if (!payload.segments.length) {
      message.warning('请至少填写一段航段');
      return;
    }

    setGenerating(true);
    try {
      const res = await axios.post(`${API}/generate`, payload, {
        headers: headers(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${payload.fileName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success(`已生成 ${payload.passengers.length} 页行程单`);
    } catch (e) {
      // responseType=blob 时错误体也是 blob，要读出来才拿得到后端的 error 文本
      let msg = '生成失败';
      try {
        const text = await e.response?.data?.text?.();
        if (text) msg = JSON.parse(text).error || msg;
      } catch (_) { /* 保持默认文案 */ }
      message.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    if (!validPassengers.length) {
      message.warning('请先添加乘客');
      return;
    }
    let payload;
    try {
      payload = await buildPayload();
    } catch (e) {
      message.warning('请先补齐必填项');
      return;
    }
    if (!payload.segments.length) {
      message.warning('请至少填写一段航段');
      return;
    }
    setGenerating(true);
    try {
      // 预览只出第一位乘客，避免大团等待
      const res = await axios.post(
        `${API}/generate`,
        { ...payload, passengers: payload.passengers.slice(0, 1) },
        { headers: headers(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      message.error('预览失败');
    } finally {
      setGenerating(false);
    }
  };

  // -------------------------------------------------------------- 设置

  const openSettings = () => {
    settingsForm.setFieldsValue(settings || {});
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    const values = await settingsForm.validateFields();
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API}/settings`, values, { headers: headers() });
      if (res.data.success) {
        setSettings(res.data.data);
        message.success('默认值已保存，下次打开自动带入');
        setSettingsOpen(false);
      }
    } catch (e) {
      message.error(e.response?.data?.error || '保存失败');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/logo`, fd, { headers: headers() });
      if (res.data.success) {
        message.success('抬头图片已更新');
        setSettings((s) => ({ ...(s || {}), hasLogo: true, hasCustomLogo: true }));
        loadLogo();
      }
    } catch (e) {
      message.error(e.response?.data?.error || '上传失败');
    }
    return false;
  };

  const removeLogo = async () => {
    try {
      await axios.delete(`${API}/logo`, { headers: headers() });
      setSettings((s) => ({ ...(s || {}), hasLogo: false, hasCustomLogo: false }));
      setLogoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      message.success('已删除抬头图片');
    } catch (e) {
      message.error('删除失败');
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={<Space><FilePdfOutlined />团票行程单</Space>}
        extra={
          <Button icon={<SettingOutlined />} onClick={openSettings}>
            默认值与抬头
          </Button>
        }
        styles={{ body: { paddingTop: 12 } }}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="上传含「姓名 / 护照号 / 票号」三列的 Excel，补齐 PNR 与航段后生成 IATA 行程单，每位乘客一页。"
        />

        {/* 第一步：乘客 */}
        <Title level={5} style={{ marginTop: 0 }}>1. 乘客数据</Title>
        <Space style={{ marginBottom: 12 }} wrap>
          <Upload beforeUpload={handleExcel} showUploadList={false} accept=".xlsx,.xlsm,.csv">
            <Button icon={<UploadOutlined />} loading={parsing}>上传 Excel</Button>
          </Upload>
          <Button icon={<PlusOutlined />} onClick={() => setPassengers((l) => [...l, emptyPassenger()])}>
            添加一行
          </Button>
          {passengers.length > 0 && (
            <Popconfirm title="清空全部乘客？" onConfirm={() => setPassengers([emptyPassenger()])}>
              <Button danger type="text">清空</Button>
            </Popconfirm>
          )}
          <Tag color="blue">共 {validPassengers.length} 人</Tag>
        </Space>
        <Table
          rowKey="_key"
          size="small"
          columns={passengerColumns}
          dataSource={passengers}
          pagination={false}
          scroll={{ y: 320 }}
        />

        <Divider />

        {/* 第二步：公共信息 */}
        <Title level={5}>2. 全团共用信息</Title>
        <Form form={form} layout="vertical" size="middle">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="airlinePnr" label="AIRLINE PNR" rules={[{ required: true, message: '请填写' }]}>
                <Input placeholder="NXGZEX" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="systemPnr" label="1E PNR">
                <Input placeholder="NXGZEX" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="dateOfIssue" label="DATE OF ISSUE" rules={[{ required: true, message: '请选择' }]}>
                {/* 输入框里直接显示成品格式 16 JUL 2026 */}
                <DatePicker format={fmtIssueDate} style={{ width: '100%' }} placeholder="16 JUL 2026" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="idPrefix" label="ID NUMBER 前缀" tooltip="护照号前统一加的前缀，行程单惯例是 PP">
                <Input placeholder="PP" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="issuingAirline" label="ISSUING AIRLINE">
                <Input placeholder="HAINAN AIRLINES" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="issuingAgent" label="ISSUING AGENT">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="iataCode" label="IATA CODE">
                <Input placeholder="4073000" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Form.Item name="tel" label="TEL">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Form.Item name="fax" label="FAX">
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item name="formOfPayment" label="FORM OF PAYMENT">
                <Input placeholder="CASH(NZD)" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <Form.Item name="restrictions" label="RESTRICTIONS">
                <Input placeholder="NON-END/RER/REB/REF" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider />

        {/* 第三步：航段 */}
        <Title level={5}>3. 航段</Title>
        <Space style={{ marginBottom: 12 }}>
          <Button icon={<PlusOutlined />} onClick={() => setSegments((l) => [...l, emptySegment()])}>
            添加航段
          </Button>
        </Space>
        <Table
          rowKey="_key"
          size="small"
          columns={segmentColumns}
          dataSource={segments}
          pagination={false}
          scroll={{ x: 1420 }}
        />

        <Divider />

        <Space>
          <Button onClick={handlePreview} loading={generating}>预览首页</Button>
          <Button type="primary" icon={<FilePdfOutlined />} loading={generating} onClick={handleGenerate}>
            生成 PDF（{validPassengers.length} 页）
          </Button>
        </Space>
      </Card>

      <Modal
        title="默认值与抬头"
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        onOk={saveSettings}
        confirmLoading={savingSettings}
        okText="保存默认值"
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="这里保存的是每次新建行程单时自动带入的默认值，不影响已填写的内容。"
        />

        <Form form={settingsForm} layout="vertical" size="middle">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="issuingAirline" label="ISSUING AIRLINE"><Input /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="issuingAgent" label="ISSUING AGENT"><Input /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="iataCode" label="IATA CODE"><Input /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tel" label="TEL"><Input /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fax" label="FAX"><Input /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="formOfPayment" label="FORM OF PAYMENT"><Input /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="restrictions" label="RESTRICTIONS"><Input /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="idPrefix" label="ID NUMBER 前缀"><Input /></Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider style={{ margin: '4px 0 16px' }} />

        <Title level={5} style={{ marginTop: 0 }}>抬头图片</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          行程单左上角的 IATA 标识。系统已内置标准图样，仅在需要替换时上传。
        </Text>
        <Space align="start" size={16}>
          <div
            style={{
              width: 220, height: 80, border: '1px dashed #d9d9d9', borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#fafafa', overflow: 'hidden',
            }}
          >
            {logoUrl
              ? <img src={logoUrl} alt="抬头" style={{ maxWidth: '100%', maxHeight: '100%' }} />
              : <Text type="secondary"><PictureOutlined /> 加载中</Text>}
          </div>
          <Space direction="vertical">
            <Upload beforeUpload={handleLogoUpload} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />}>上传图片</Button>
            </Upload>
            {settings?.hasCustomLogo && (
              <Popconfirm title="恢复内置抬头？" onConfirm={removeLogo}>
                <Button danger type="text" icon={<DeleteOutlined />}>恢复内置</Button>
              </Popconfirm>
            )}
          </Space>
        </Space>
      </Modal>
    </div>
  );
}

export default GroupTicketItinerary;
